import moment from "moment";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Select from "../../../../parts/inputs/select";
import {
  addDays,
  nextFlexibleWeekStartDate,
} from "../../../../utils/flexible-program";

/** How far ahead the Start date list reaches. */
const WEEKS_TO_SHOW = 24;

const prettyShort = (yyyymmdd) =>
  moment(String(yyyymmdd), "YYYYMMDD").format("MMM D");
const prettyFull = (yyyymmdd) =>
  moment(String(yyyymmdd), "YYYYMMDD").format("MMM D, YYYY");

/**
 * Asks the coach when a new week should take over, for either "Add new week"
 * or "Duplicate week" - both need the same answer and the same rule, so they
 * share this one modal rather than each inventing their own date picker.
 *
 * Only shown while the governing week is actually repeating: a non-repeating
 * program has nothing live to protect, so both callers skip this entirely and
 * go straight to the next lattice boundary (see FlexibleProgram.js and
 * WeekRow's duplicate button).
 *
 * Only one weekday a week is ever a valid start - every week this program has
 * ever run measures from the same anchor weekday - so a full calendar made the
 * coach hunt for the one selectable day in each row. Listing the weeks
 * directly (every lattice boundary from the next one onward, `earliest + 7k`)
 * says the same thing a calendar + `min` + `dateValidator` used to enforce,
 * without needing any of that validation machinery - the list can only ever
 * contain valid weeks.
 */
const WeekStartModal = ({ cycle, title, onClose, onConfirm }) => {
  const { t } = useTranslation("coaching-portal");
  const today = Number(moment().format("YYYYMMDD"));
  const earliest = nextFlexibleWeekStartDate(cycle, today);

  const weekOptions = Array.from({ length: WEEKS_TO_SHOW }, (_, index) => {
    const weekStart = addDays(earliest, index * 7);
    return {
      value: String(weekStart),
      text: t("flexible.weekStart.weekOption", {
        start: prettyShort(weekStart),
        end: prettyFull(addDays(weekStart, 6)),
      }),
    };
  });

  const [startDate, setStartDate] = useState(earliest);

  useEffect(() => {
    const handleKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  return (
    <div className="wk-modal-overlay open" onClick={onClose}>
      <div
        className="wk-modal week-start-modal"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="wk-modal-header">
          <h2 className="alt">{title}</h2>
          <button
            type="button"
            className="wk-modal-close"
            data-button-type="tertiary"
            data-icon-only
            onClick={onClose}
            aria-label={t("flexible.manager.cancel")}
          >
            <img src="/img/new-icons/red-cross.svg" alt="" />
          </button>
        </header>

        <div className="wk-modal-body">
          <div className="group">
            <label>{t("flexible.weekStart.label")}</label>
            <Select
              name="startDate"
              options={weekOptions}
              value={String(startDate)}
              onChange={(value) => setStartDate(Number(value))}
              overlay={true}
            />
            <span className="text-[0.75rem] opacity-60">
              {t("flexible.weekStart.hint")}
            </span>
          </div>
        </div>

        <footer className="wk-modal-footer">
          <button type="button" data-button-type="secondary" onClick={onClose}>
            {t("flexible.manager.cancel")}
          </button>
          <button
            type="button"
            data-button-type="primary"
            onClick={() => onConfirm(startDate)}
          >
            {t("flexible.weekStart.confirm")}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default WeekStartModal;
